import React, { useState, useEffect } from "react";
import Feedback from "./Feedback";
import {
  GreenButton,
  RedButton
} from "../../../globals/components/button";
import styled from "styled-components";
import background from "../assets/background.png";
import trophy from "../assets/trophy.png";
import { Scale } from 'react-scaling';

import useAuth from "../../auth/hooks/useAuth";
import axios from "axios";

export default function AssessmentResult(props) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [savedSubmission, setSavedSubmission] = useState(false);
  const {
    correct,
    incorrect,
    qanswers,
    onBack,
    parent
  } = props;

  const { state: authState } = useAuth();

  useEffect(() => {
    if (authState != null && authState.access_token != null) {
      submitResults();
    }
  }, [authState]);

  const submitResults = () => {
    const groupByTopic = (list) => list.reduce((byTopics, question) => {
      const key = question.topic_id || "General";
      byTopics[key] = byTopics[key] || [];
      byTopics[key].push(question);
      return byTopics;
    }, {});

    const correctByTopics = groupByTopic(correct);
    const incorrectByTopics = groupByTopic(incorrect);
    const allTopicKeys = [...new Set([...Object.keys(correctByTopics), ...Object.keys(incorrectByTopics)])];

    const topicstats = allTopicKeys.map(key => {
      const num_correct = correctByTopics[key]?.length || 0;
      const num_incorrect = incorrectByTopics[key]?.length || 0;
      return {
        topic_id: isNaN(key) ? undefined : parseInt(key),
        topic_key: isNaN(key) ? key : undefined,
        num_correct,
        num_asked: num_correct + num_incorrect,
        average_ans_time: 0,
        total_time_taken: 0
      };
    });

    // --- INTEGRATED PRACTICE GAME LOGIC FOR QUESTIONS ANSWERED ---
    var questionsAnswered = [];
    
    if (qanswers && qanswers.length > 0) {
        // CHRONOLOGICAL ROUTE: If qanswers exists (Assessments), preserve exact user timeline
        questionsAnswered = qanswers.map(element => ({
            question: element.sentence || element.question,
            correct: element.correct,         // Uses the true/false boolean set at submission time
            answer: element.answer || ""      // Captured pupil text response
        }));
    } else {
        // FALLBACK ROUTE: For Practice.js or any module without qanswers
        incorrect.forEach(element => {
            let data = {
                question: element.sentence || element.question,
                correct: false,
                answer: element.answer || ""
            };
            questionsAnswered.push(data);
        });

        correct.forEach(element => {
            let data = {
                question: element.sentence || element.question,
                correct: true,
                answer: element.word
            };
            questionsAnswered.push(data);
        });
    }

    axios({
      url: `${process.env.REACT_APP_API}/submissions`,
      method: "post",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authState.access_token}`
      },
      data: {
        "homework_id": parent?.homework_id,
        "topicstats": topicstats,
        "platform": "Assessment|WEB|" + process.env.REACT_APP_VERSION_NO,
        "custom": parent?.custom,
        "game_id": parent?.gameData?.id,
        "questionsAnswered": JSON.stringify(questionsAnswered),
        // Included Duel fields if applicable
        ...(parent?.isDuel && parent?.duelData && {
            "is_duel": "true",
            "duel_id": parent?.duelData.id.toString(),
        }),
      }
    }).then((response) => {
      if (response.status === 201) setSavedSubmission(true);
    }).catch((e) => console.log(e));
  };

  // Prepare feedback data so Feedback.js renders correctly
// Transform the 'incorrect' array into the format Feedback.js expects
const feedbackData = incorrect.map(item => ({
    // 1. Give it the sentence with the $
    question: item.sentence || item.question, 
    
    // 2. Map 'answer' to 'incorrect' key for the red box
    incorrect: item.answer || "", 
    
    // 3. Map 'word' to 'correct' key for the green box
    correct: item.word 
}));

  return (
    <ApplicationResult>
      <Scale scaleTo={1720}>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
          <ResultImage>
            <div style={{ position: "relative", height: "300px" }}>
              <Banner>Assessment Complete</Banner>
            </div>
          </ResultImage>

          {showFeedback ? (
            <ResultText style={{ maxHeight: "500px", overflowY: "scroll" }}>
              Feedback
              <Feedback feedback={feedbackData} />
            </ResultText>
          ) : (
            <ResultText style={{ maxHeight: "500px", overflowY: "scroll" }}>
              <ResultScores>
                <Result>
                  <div style={{ color: "#42B231" }}>Correct:</div>
                  <ResultValue>{correct.length}</ResultValue>
                  <div style={{ color: "#EF3E37" }}>Incorrect:</div>
                  <ResultValue>{incorrect.length}</ResultValue>
                </Result>
              </ResultScores>
              {savedSubmission && (
                <div style={{ fontSize: "0.5em", color: "#42B231", marginTop: "20px", textAlign: "center" }}>
                  ✓ Result submitted
                </div>
              )}
            </ResultText>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
          <RedButton
            onClick={onBack}
            style={{ width: "40%", height: "60px", fontSize: "30px", margin: "20px" }}
          >
            Back
          </RedButton>
          {incorrect.length > 0 && (
            <GreenButton
              onClick={() => setShowFeedback(!showFeedback)}
              style={{ width: "40%", height: "60px", fontSize: "30px", margin: "20px" }}
            >
              {showFeedback ? "Hide Feedback" : "Show Feedback"}
            </GreenButton>
          )}
        </div>
      </Scale>
    </ApplicationResult>
  );
}

const ApplicationResult = styled.div`
  height: 100%;
  z-index: 10;
  background: #fff url(${background});
  padding: 40px;
  font-size: 3em;
  border: solid 2px #683d8c;
  display: flex;
  flex-direction: column;
`;

const ResultScores = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  flex-direction: column;
`;

const Result = styled.div`
  display: flex;
  margin-top: 20px;
`;

const ResultValue = styled.div`
  margin: 0px 20px;
  border: solid #e4e4e4 2px;
  background: #fff;
  padding: 0px 15px;
`;

const ResultText = styled.div`
  background: #fff;
  border: solid #683d8c 2px;
  width: 50%;
  border-radius: 10px;
  padding: 10px;
  color: #683d8c;
  font-weight: 600;
  font-size: 1.2em;
`;

const ResultImage = styled.div`
  width: 40%;
  background-image: url(${trophy});
  background-size: auto;
  background-repeat: no-repeat;
  background-position: top;
  display: flex;
  flex-direction: column;
  align-self: center;
`;

const Banner = styled.div`
  position: absolute;
  left: 50%;
  margin: 100px -200px;
  width: 400px;
  height: 60px;
  border: 1px solid #e88f12;
  line-height: 60px;
  font-weight: 600;
  text-align: center;
  color: #fff;
  background: rgba(255, 186, 32, 1);
  border-radius: 4px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.15) inset, 0 6px 10px rgba(0, 0, 0, 0.15);
  &::before, ::after {
    content: "";
    position: absolute;
    z-index: -1;
    left: -70px;
    top: 24px;
    width: 40px;
    height: 0px;
    border: 30px solid #e88f12;
    border-right: 20px solid #c97e11;
    border-bottom-color: #e88f12;
    border-left-color: transparent;
    transform: rotate(-5deg);
  }
  &::after {
    left: auto;
    right: -70px;
    border-left: 20px solid #c97e11;
    border-right: 30px solid transparent;
    transform: rotate(5deg);
  }
`;